import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import BASE_URL from "../../config/api";
import { BsSend, BsPersonFill, BsCircleFill, BsXCircle } from "react-icons/bs";

const SOCKET_URL = BASE_URL;

export default function AdminChats() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [visitorTyping, setVisitorTyping] = useState(false);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);

  // Load all sessions on mount
  useEffect(() => {
    axios.get(`${BASE_URL}/chat/sessions`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
    }).then((res) => {
      setSessions(res.data.data ?? []);
    });
  }, []);

  // Connect socket as admin
  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ["websocket"] });
    socketRef.current = socket;
    socket.emit("admin:join", { token: localStorage.getItem("userToken") });

    socket.on("chat:new_session", (session) => {
      setSessions((prev) => {
        const exists = prev.find((s) => s.sessionId === session.sessionId);
        return exists ? prev : [session, ...prev];
      });
    });

    socket.on("chat:session_update", (update) => {
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === update.sessionId ? { ...s, ...update } : s
        )
      );
    });

    socket.on("chat:message", (msg) => {
      setMessages((prev) => [...prev, msg]);
    });

    socket.on("chat:history", (history) => {
      setMessages(history);
    });

    socket.on("chat:typing", ({ sender }) => {
      if (sender === "visitor") {
        setVisitorTyping(true);
        setTimeout(() => setVisitorTyping(false), 2000);
      }
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, visitorTyping]);

  const openSession = (session) => {
    setActive(session);
    setMessages([]);
    socketRef.current?.emit("admin:open_session", {
      sessionId: session.sessionId,
      token: localStorage.getItem("userToken"),
    });
    // Clear unread badge
    setSessions((prev) =>
      prev.map((s) => (s.sessionId === session.sessionId ? { ...s, unreadCount: 0 } : s))
    );
  };

  const sendMessage = () => {
    const text = input.trim();
    if (!text || !active) return;
    socketRef.current?.emit("chat:message", {
      sessionId: active.sessionId,
      text,
      sender: "admin",
    });
    setInput("");
  };

  const closeSession = () => {
    if (!active) return;
    socketRef.current?.emit("chat:close", {
      sessionId: active.sessionId,
      token: localStorage.getItem("userToken"),
    });
    setSessions((prev) =>
      prev.map((s) => (s.sessionId === active.sessionId ? { ...s, status: "closed" } : s))
    );
    setActive((a) => ({ ...a, status: "closed" }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else {
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        socketRef.current?.emit("chat:typing", { sessionId: active?.sessionId, sender: "admin" });
      }, 300);
    }
  };

  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return formatTime(ts);
    return d.toLocaleDateString([], { day: "numeric", month: "short" });
  };

  const totalUnread = sessions.reduce((n, s) => n + (s.unreadCount || 0), 0);

  return (
    <div className="flex h-[calc(100vh-64px)] bg-white font-Montserrat overflow-hidden">

      {/* Session list */}
      <div className="w-72 border-r border-gray-100 flex flex-col flex-shrink-0">
        <div className="px-4 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900 text-base">Live Chats</h2>
            {totalUnread > 0 && (
              <span className="bg-[#1877F2] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                {totalUnread}
              </span>
            )}
          </div>
          <p className="text-gray-400 text-xs mt-0.5">{sessions.length} conversations</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sessions.length === 0 && (
            <p className="text-center text-gray-400 text-sm p-6 mt-4">No chats yet</p>
          )}
          {sessions.map((s) => (
            <button
              key={s.sessionId}
              onClick={() => openSession(s)}
              className={`w-full text-left px-4 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                active?.sessionId === s.sessionId ? "bg-blue-50 border-l-2 border-l-[#1877F2]" : ""
              }`}
            >
              <div className="w-9 h-9 rounded-full bg-[#1877F2]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <BsPersonFill className="text-[#1877F2] text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <p className="font-semibold text-gray-900 text-sm truncate">{s.visitorName}</p>
                  <p className="text-gray-400 text-[10px] flex-shrink-0">{formatDate(s.updatedAt || s.createdAt)}</p>
                </div>
                <p className="text-gray-500 text-xs truncate mt-0.5">
                  {s.lastMessage?.text || s.messages?.[s.messages.length - 1]?.text || "No messages yet"}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`flex items-center gap-1 text-[10px] font-medium ${
                    s.status === "active" ? "text-green-500" : "text-gray-400"
                  }`}>
                    <BsCircleFill className="text-[6px]" />
                    {s.status === "active" ? "Active" : "Closed"}
                  </span>
                  {s.unreadCount > 0 && (
                    <span className="bg-[#1877F2] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {s.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat window */}
      {active ? (
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1877F2]/10 flex items-center justify-center">
                <BsPersonFill className="text-[#1877F2] text-lg" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">{active.visitorName}</p>
                <p className="text-gray-400 text-xs">
                  {active.visitorPhone && `${active.visitorPhone} · `}
                  {active.visitorEmail || "No email provided"}
                </p>
              </div>
            </div>
            {active.status === "active" && (
              <button
                onClick={closeSession}
                className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 px-3 py-1.5 rounded-full transition-colors"
              >
                <BsXCircle className="text-sm" />
                Close chat
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[65%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.sender === "admin"
                      ? "bg-[#1877F2] text-white rounded-br-sm"
                      : "bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100"
                  }`}
                >
                  <p>{msg.text}</p>
                  <p className={`text-[10px] mt-1 ${msg.sender === "admin" ? "text-white/60 text-right" : "text-gray-400"}`}>
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            ))}
            {visitorTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 shadow-sm px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          {active.status === "active" ? (
            <div className="px-4 py-3 border-t border-gray-100 bg-white flex gap-2 flex-shrink-0">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a reply..."
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2]"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim()}
                className="w-10 h-10 flex items-center justify-center bg-[#1877F2] hover:bg-[#0e5fc7] disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-white transition-colors"
                aria-label="Send reply"
              >
                <BsSend className="text-sm" />
              </button>
            </div>
          ) : (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-center">
              <p className="text-gray-400 text-sm">This conversation is closed</p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#1877F2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <BsPersonFill className="text-[#1877F2] text-2xl" />
            </div>
            <p className="font-semibold text-gray-700">Select a conversation</p>
            <p className="text-gray-400 text-sm mt-1">Choose a chat from the left to start replying</p>
          </div>
        </div>
      )}
    </div>
  );
}
